import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { LoginCredentials } from '../../types/auth';
import { Button, Field, Input } from '../ui';
import { AUTH_BUTTON, AUTH_LINK, AuthActions, AuthFootnote, AuthScreen, FormNotice, PasswordInput } from './AuthShell';

interface LoginFormProps {
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<LoginCredentials>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginCredentials> = {};

    if (!credentials.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!credentials.password) {
      newErrors.password = 'Password is required';
    } else if (credentials.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await login(credentials);
      onSuccess?.();
    } catch (error: any) {
      console.error('Login error:', error);
      let message = 'Login failed. Please try again.';
      
      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.response?.status === 401) {
        message = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error?.response?.status === 400) {
        message = 'Invalid data provided. Please check your input and try again.';
      } else if (error?.response?.status >= 500) {
        message = 'Server error occurred. Please try again later.';
      } else if (error?.code === 'NETWORK_ERROR' || !error?.response) {
        message = 'Network error. Please check your connection and try again.';
      }
      
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name as keyof LoginCredentials]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <AuthScreen title="Sign in" aside>
      <form className="flex flex-1 flex-col" onSubmit={handleSubmit} noValidate>
        <div className="space-y-3.5">
          <Field label="Email" htmlFor="email" error={errors.email}>
            <Input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={credentials.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              placeholder="you@company.com"
            />
          </Field>

          <Field label="Password" htmlFor="password" error={errors.password}>
            <PasswordInput
              id="password"
              name="password"
              autoComplete="current-password"
              required
              value={credentials.password}
              onChange={handleChange}
              aria-invalid={!!errors.password}
            />
          </Field>
        </div>

        <AuthActions>
          {submitError && <FormNotice>{submitError}</FormNotice>}
          <Button type="submit" variant="primary" loading={submitting} className={AUTH_BUTTON}>
            {submitting ? 'Signing in...' : 'Sign in'}
          </Button>
          <AuthFootnote>
            No account?{' '}
            <a href="/register" className={AUTH_LINK}>
              Register
            </a>
          </AuthFootnote>
        </AuthActions>
      </form>
    </AuthScreen>
  );
};
